import axios from '../config/axiosConfig';

const API_URL = '/auth-profiles';

const getAll = async (projectId) => {
  const response = await axios.get(API_URL, { params: { projectId } });
  return response.data;
};

const getById = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

const create = async (data) => {
  const response = await axios.post(API_URL, data);
  return response.data;
};

const update = async (id, data) => {
  const response = await axios.put(`${API_URL}/${id}`, data);
  return response.data;
};

const remove = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

// Pull the full authenticated cookie jar (incl. HttpOnly session cookies) from
// recent proxy traffic into this profile, matched to the profile's user.
const syncCookiesFromTraffic = async (id, webTargetId) => {
  const response = await axios.post(`${API_URL}/${id}/sync-cookies-from-traffic`, { webTargetId });
  return response.data;
};

const validate = async (id) => {
  const response = await axios.post(`${API_URL}/${id}/validate`);
  return response.data;
};

const promote = async (data) => {
  const response = await axios.post(`${API_URL}/promote`, data);
  return response.data;
};

// Create a profile from captured proxy traffic (authenticated request)
const getCaptureCandidates = async (webTargetId) => {
  const response = await axios.get(`${API_URL}/capture-candidates`, { params: { webTargetId } });
  return response.data;
};

const createFromCapture = async (data) => {
  const response = await axios.post(`${API_URL}/from-capture`, data);
  return response.data;
};

// Token extraction methods (Phase 3)
const extractFromHtml = async (html) => {
  const response = await axios.post(`${API_URL}/extract-from-html`, { html });
  return response.data;
};

const extractFromJson = async (json) => {
  const response = await axios.post(`${API_URL}/extract-from-json`, { json });
  return response.data;
};

const extractFromHar = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_URL}/extract-from-har`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

const applyExtraction = async (profileId, token) => {
  const response = await axios.post(`${API_URL}/${profileId}/apply-extraction`, { token });
  return response.data;
};

const createFromExtraction = async (projectId, name, token) => {
  const response = await axios.post(`${API_URL}/create-from-extraction`, {
    projectId,
    name,
    token
  });
  return response.data;
};

// Token validation and refresh methods (Phase 4)
const getTokenStatus = async (profileId) => {
  const response = await axios.get(`${API_URL}/${profileId}/token-status`);
  return response.data;
};

const refreshToken = async (profileId) => {
  const response = await axios.post(`${API_URL}/${profileId}/refresh`);
  return response.data;
};

const validateJwt = async (token) => {
  const response = await axios.post(`${API_URL}/validate-jwt`, { token });
  return response.data;
};

// Auth type display configs
const authTypeConfig = {
  none: { label: 'None', icon: 'ban', color: 'gray' },
  basic: { label: 'Basic Auth', icon: 'key', color: 'blue' },
  bearer: { label: 'Bearer Token', icon: 'shield-alt', color: 'green' },
  jwt: { label: 'JWT', icon: 'fingerprint', color: 'purple' },
  apikey: { label: 'API Key', icon: 'key', color: 'yellow' },
  cookie: { label: 'Cookie', icon: 'cookie-bite', color: 'orange' },
  oauth2_client_credentials: { label: 'OAuth2 Client', icon: 'lock', color: 'teal' },
  oauth2_authorization_code: { label: 'OAuth2 Auth Code', icon: 'lock', color: 'teal' },
  form_login: { label: 'Form Login', icon: 'sign-in-alt', color: 'indigo' },
  custom_header: { label: 'Custom Header', icon: 'edit', color: 'pink' },
  proxy_capture: { label: 'Proxy Captured', icon: 'wifi', color: 'cyan' }
};

const getAuthTypeConfig = (type) => authTypeConfig[type] || authTypeConfig.none;

const authProfileService = {
  getAll,
  getById,
  create,
  update,
  remove,
  validate,
  promote,
  getCaptureCandidates,
  createFromCapture,
  extractFromHtml,
  extractFromJson,
  extractFromHar,
  applyExtraction,
  createFromExtraction,
  getTokenStatus,
  refreshToken,
  validateJwt,
  syncCookiesFromTraffic,
  authTypeConfig,
  getAuthTypeConfig
};

export default authProfileService;
